import { useCallback } from 'react';
import type React from 'react';
import type { HandsontableWrapperRef } from '../HandsontableWrapperSimple';
import type { TrackedTableRef } from '../tracking';

export type AddRowTableRef = React.RefObject<HandsontableWrapperRef | TrackedTableRef | null>;

/**
 * Добавление строки в конец таблицы.
 *
 * Общая логика для кнопки «+» в LocalTableToolbar и для кнопки в пустом
 * состоянии таблицы (EmptyAddRowOverlay) — обе работают с одним инстансом
 * Handsontable, поэтому поведение (позиция новой строки, страница, фокус)
 * обязано совпадать.
 */
export const useAddTableRow = (
  tableRef: AddRowTableRef | undefined,
  onRowAdded?: (rowIndex: number) => void,
) => useCallback(() => {
  const hot = tableRef?.current?.getHotInstance?.();
  if (!hot) return;

  const rowCount = hot.countRows();
  hot.alter('insert_row_below', rowCount, 1, 'addRow');

  // После вставки переходим на последнюю страницу и ставим курсор в первую
  // ячейку новой строки — пользователь сразу видит её и может заполнять.
  // rAF нужен: alter() синхронный, но плагин пагинации обновляет DOM асинхронно.
  requestAnimationFrame(() => {
    // Инстанс могли уничтожить между alter() и кадром (размонтирование таблицы,
    // перезагрузка данных) — обращение к API уничтоженного HoT бросает исключение.
    if (hot.isDestroyed) return;
    const pagination = hot.getPlugin('pagination') as { isEnabled?: () => boolean; lastPage?: () => void } | null;
    if (pagination?.isEnabled?.() && pagination?.lastPage) {
      pagination.lastPage();
    }
    const visualRow = hot.toVisualRow(rowCount);
    if (visualRow != null && visualRow >= 0) {
      hot.selectCell(visualRow, 0);
    }
  });

  onRowAdded?.(rowCount);
}, [tableRef, onRowAdded]);
